import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { backofficeApi } from '@/lib/api-client'
import { facetToQuery, isFacetActive } from '@/lib/facet'
import { DESK_REFETCH_MS, visibleTabRefetch } from '@/lib/query-client'
import type { DeskFacets } from '@/lib/desk-url'

export const agreementKeys = {
  all: ['agreements'] as const,
  list: (filters: object) => [...agreementKeys.all, 'list', filters] as const,
  total: (filters: object) => [...agreementKeys.all, 'total', filters] as const,
  options: ['agreements', 'filter-options'] as const,
}

function bookParams(filters: DeskFacets, page: number, pageSize: number, includeTotal: boolean) {
  return {
    q: filters.q || undefined,
    page,
    page_size: pageSize,
    include_total: includeTotal,
    ...facetToQuery('status', filters.status),
    ...facetToQuery('utm_source', filters.utm_source),
    ...facetToQuery('utm_medium', filters.utm_medium),
    ...facetToQuery('utm_campaign', filters.utm_campaign),
    ...facetToQuery('kyc_tier', filters.kyc_tier),
    ...facetToQuery('risk_band', filters.risk_band),
  }
}

export function useAgreementBook(
  filters: DeskFacets,
  page: number,
  pageSize: number,
  sort: { sort?: string; order?: string } = {},
) {
  const params = { ...bookParams(filters, page, pageSize, false), ...sort }
  return useQuery({
    queryKey: agreementKeys.list(params),
    queryFn: () => backofficeApi.openingBook(params),
    placeholderData: keepPreviousData,
    ...visibleTabRefetch(DESK_REFETCH_MS),
  })
}

export function useAgreementTotal(filters: DeskFacets) {
  const params = bookParams(filters, 1, 0, true)
  return useQuery({
    queryKey: agreementKeys.total(params),
    queryFn: () => backofficeApi.openingBook({ ...params, page: 1, page_size: 1, include_total: true }),
    select: (data) => data.total ?? 0,
    placeholderData: keepPreviousData,
    ...visibleTabRefetch(DESK_REFETCH_MS),
  })
}

export function useAgreementFilterOptions(enabled: boolean) {
  return useQuery({
    queryKey: agreementKeys.options,
    queryFn: () => backofficeApi.openingFilterOptions(),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function deskFacetsActive(filters: DeskFacets): boolean {
  return Boolean(
    filters.q ||
      isFacetActive(filters.status) ||
      isFacetActive(filters.utm_source) ||
      isFacetActive(filters.utm_medium) ||
      isFacetActive(filters.utm_campaign) ||
      isFacetActive(filters.kyc_tier) ||
      isFacetActive(filters.risk_band),
  )
}
